import { cn } from '@pesto/utils';
import * as React from 'react';

import { Typography } from './Typography';

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('bg-card text-card-foreground overflow-hidden rounded-lg border shadow-sm', className)}
    {...props}
  />
));

Card.displayName = 'Card';

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col space-y-1.5 p-6', className)} {...props} />
  ),
);

CardHeader.displayName = 'CardHeader';

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-2xl font-semibold leading-none tracking-tight', className)} {...props} />
  ),
);

CardTitle.displayName = 'CardTitle';

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-muted-foreground text-sm', className)} {...props} />
  ),
);

CardDescription.displayName = 'CardDescription';

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />,
);

CardContent.displayName = 'CardContent';

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex items-center p-6 pt-0', className)} {...props} />
  ),
);

CardFooter.displayName = 'CardFooter';

interface CardProps {
  className?: string;
  title?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
}

const CardComponent = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, title, footer, children, ...props }, ref) => {
    return (
      <Card ref={ref} className={cn('rounded-lg', className)} {...props}>
        {title && (
          <div className="bg-muted-foreground/5 border-b p-4">
            <Typography variant="headerSmall">{title}</Typography>
          </div>
        )}
        <div className="p-4">{children}</div>
        {footer && <div className="bg-muted-foreground/5 border-t p-4">{footer}</div>}
      </Card>
    );
  },
);

CardComponent.displayName = 'CardComponent';

export { Card, CardComponent, CardContent, CardDescription, CardFooter, CardHeader, CardTitle };
